import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SummaryBar from "../app/components/SummaryBar";
import ToolCard from "../app/components/ToolCard";
import UsageChart from "../app/components/UsageChart";
import { dashboardFromSnapshot } from "../lib/cloud/dashboard";
import { monthlySeriesFromRows } from "../lib/cloud/history";
import { relativeTime } from "../lib/format";
import { TOOLS } from "../lib/tools";
import type { MonthlySeries } from "../lib/types";
import { loadSnapshot, LoadError, type LoadResult } from "./client";
import { loadPrices, type Prices } from "./prices";
import SettingsForm from "./Settings";
import {
  clearSettings,
  isConfigured,
  loadSettings,
  saveSettings,
  type Settings,
} from "./store";

/** Auto-refresh cadence. Matches the pushers' default publish interval. */
const REFRESH_MS = 5 * 60_000;

/**
 * The data currently on screen. `now` is captured when the load lands rather
 * than read during render, so every relative time refers to one instant and
 * none of them drift between repaints.
 */
interface Loaded {
  result: LoadResult;
  now: number;
}

/**
 * The outcome of the *most recent* load attempt — deliberately separate from
 * {@link Loaded}.
 *
 * Folding these into one state was a real bug: a transient failure on the
 * five-minute background refresh would replace a perfectly good dashboard with
 * an error screen, discarding data that was valid seconds earlier. Held apart,
 * a failed refresh becomes a banner *above* data that stays on screen, which is
 * what a monitoring page should do when it briefly can't reach its source.
 */
type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string };

export default function App() {
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const [editing, setEditing] = useState(false);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [load, setLoad] = useState<LoadState>({ kind: "idle" });
  const [prices, setPrices] = useState<Prices | null>(null);

  /**
   * Monotonic request id. A result is applied only if no newer request has
   * started since — otherwise a slow attempt against *old* settings can land
   * after the user has corrected them and be displayed as if it described the
   * new ones. A single in-flight boolean could not express that: it dropped the
   * newer request instead of superseding the older one.
   */
  const generation = useRef(0);

  const refresh = useCallback(async () => {
    if (!isConfigured(settings)) return;
    const gen = ++generation.current;
    setLoad({ kind: "loading" });
    try {
      const result = await loadSnapshot(settings);
      if (gen !== generation.current) return; // superseded
      setLoaded({ result, now: Date.now() });
      setLoad({ kind: "idle" });
    } catch (err) {
      if (gen !== generation.current) return;
      setLoad({
        kind: "error",
        message:
          err instanceof LoadError
            ? err.message
            : `unexpected error: ${(err as Error).message}`,
      });
    }
  }, [settings]);

  useEffect(() => {
    void loadPrices().then(setPrices);
  }, []);

  useEffect(() => {
    if (!isConfigured(settings)) return;
    // Fetching is what an effect is for — synchronising with an external
    // system. The rule can't see that `refresh` sets no state until after its
    // first await, so there is no cascading render to avoid here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
    const id = setInterval(() => void refresh(), REFRESH_MS);
    return () => clearInterval(id);
  }, [settings, refresh]);

  if (editing || !isConfigured(settings)) {
    return (
      <SettingsForm
        initial={settings}
        error={load.kind === "error" ? load.message : undefined}
        onSave={(s) => {
          saveSettings(s);
          setSettings(s);
          setEditing(false);
          // Data from the previous drop describes a different source and must
          // not linger under the new one's heading.
          setLoaded(null);
          setLoad({ kind: "loading" });
        }}
        onCancel={isConfigured(settings) ? () => setEditing(false) : undefined}
      />
    );
  }

  return (
    <Dashboard
      loaded={loaded}
      load={load}
      prices={prices}
      onRefresh={refresh}
      onEdit={() => setEditing(true)}
      onForget={() => {
        clearSettings();
        setSettings(loadSettings());
        setLoaded(null);
        setLoad({ kind: "idle" });
      }}
    />
  );
}

function Dashboard({
  loaded,
  load,
  prices,
  onRefresh,
  onEdit,
  onForget,
}: {
  loaded: Loaded | null;
  load: LoadState;
  prices: Prices | null;
  onRefresh: () => void;
  onEdit: () => void;
  onForget: () => void;
}) {
  const result = loaded?.result ?? null;
  const now = loaded?.now ?? 0;

  const data = useMemo(
    () =>
      result && prices
        ? dashboardFromSnapshot(result.snapshot, prices.lookup, now)
        : null,
    [result, prices, now],
  );

  // Derived from the load's own timestamp rather than frozen at mount, so a tab
  // left open across a month boundary follows the calendar instead of showing
  // the previous month indefinitely.
  const month = useMemo(() => monthKey(now), [now]);

  const series = useMemo(
    () =>
      result && prices
        ? monthlySeriesFromRows(result.snapshot.daily, month, prices.lookup)
        : null,
    [result, prices, month],
  );

  // Month changes need no network: every row is already decrypted in memory.
  const loadMonth = useCallback(
    async (m: string): Promise<MonthlySeries | null> =>
      result && prices
        ? monthlySeriesFromRows(result.snapshot.daily, m, prices.lookup)
        : null,
    [result, prices],
  );

  // Prices are needed to render any figure, so a page waiting on them is still
  // loading. Without this the dashboard shows nothing at all, and says nothing
  // about why, whenever the price feed is slower than the drop.
  const waiting = !data && (load.kind === "loading" || !prices);

  return (
    <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-10">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* The tab's own icon file. `alt` is empty because the h1 beside it
              already names the page — announcing it twice helps no one. */}
          <img
            src="./favicon.svg"
            alt=""
            width={36}
            height={36}
            className="size-9 shrink-0"
          />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">StatsAI</h1>
            <p className="mt-0.5 text-sm text-muted">
              Usage and limits across your AI tools
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-faint sm:inline">
            {load.kind === "loading"
              ? "Loading…"
              : data?.collectedAt
                ? `Published ${relativeTime(data.collectedAt, now)}`
                : "Nothing published yet"}
          </span>
          <button
            onClick={onRefresh}
            disabled={load.kind === "loading"}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm transition-colors hover:border-border-strong disabled:opacity-40"
          >
            Refresh
          </button>
          <button
            onClick={onEdit}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm transition-colors hover:border-border-strong"
          >
            Settings
          </button>
        </div>
      </header>

      {/* A failed refresh never removes data already on screen — it says the
          figures are as of the last success and stops there. Blanking the page
          on a blip would discard readings that were valid moments ago. */}
      {load.kind === "error" && (
        <div className="mb-6 rounded-xl border border-critical/30 bg-critical/10 px-4 py-3">
          <p className="text-sm text-critical">{load.message}</p>
          <p className="mt-1 text-xs text-muted">
            {data
              ? "Showing the last successful read."
              : "Nothing has loaded yet."}
          </p>
          <div className="mt-2 flex gap-4">
            <button
              onClick={onEdit}
              className="text-xs text-muted underline transition-colors hover:text-foreground"
            >
              Check settings
            </button>
            {/* Destructive — the passphrase is unrecoverable — so this is only
                offered once there is genuinely nothing on screen to lose. */}
            {!data && (
              <button
                onClick={onForget}
                className="text-xs text-muted underline transition-colors hover:text-foreground"
              >
                Clear settings and start over
              </button>
            )}
          </div>
        </div>
      )}

      {/* A blob that couldn't be read is reported *beside* the ones that could.
          One machine publishing something this build can't decrypt must never
          blank the whole dashboard — that turns one machine's problem into total
          blindness, which is the opposite of what this page is for. */}
      {result && result.failures.length > 0 && (
        <div className="mb-6 rounded-xl border border-border bg-surface-2 px-4 py-3">
          <p className="text-xs font-medium">
            {result.failures.length} of {result.fetched} published payloads could
            not be read
          </p>
          <ul className="mt-1.5 space-y-1">
            {result.failures.map((f) => (
              <li key={`${f.machine}-${f.year}`} className="text-[11px] text-muted">
                <span className="text-foreground">
                  {f.machine} · {f.year}
                </span>{" "}
                — {f.reason}
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-[11px] text-faint">
            Everything below is from the payloads that did decrypt.
          </p>
        </div>
      )}

      {data && series && (
        <>
          <SummaryBar totals={data.totals} />

          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TOOLS.map((tool) => (
              <ToolCard
                key={tool.id}
                tool={tool}
                stats={data.tools[tool.id]}
                now={now}
                // No manual-entry slot: that form writes to a local database
                // over an API route, neither of which exists here.
                emptyHint="No machine has published this tool yet — run the pusher on the machine it belongs to."
              />
            ))}
          </section>

          <section className="mt-4">
            <UsageChart initial={series} loadMonth={loadMonth} />
          </section>

          <Provenance result={result} prices={prices} now={now} />
        </>
      )}

      {waiting && (
        <p className="rounded-xl border border-border bg-surface px-4 py-3 text-sm text-muted">
          {prices ? "Fetching and decrypting…" : "Loading model prices…"}
        </p>
      )}
    </main>
  );
}

/** YYYY-MM for an instant, in the reader's local calendar. */
function monthKey(ms: number): string {
  const d = ms > 0 ? new Date(ms) : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Which machines fed this page, and how current each one is.
 *
 * Load-bearing rather than decorative: with two machines publishing
 * independently, a card can be hours stale for a perfectly ordinary reason (that
 * laptop is shut), and there is no way to tell that from the numbers alone.
 */
function Provenance({
  result,
  prices,
  now,
}: {
  result: LoadResult | null;
  prices: Prices | null;
  now: number;
}) {
  if (!result) return null;
  return (
    <footer className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-3 text-[11px] text-faint">
      {result.snapshot.machines.map((m) => (
        <span key={m.machine}>
          <span className="text-muted">{m.machine}</span> published{" "}
          {relativeTime(m.exportedAt, now)}
        </span>
      ))}
      {result.snapshot.machines.length === 0 && (
        <span>No machines have published yet</span>
      )}
      {prices && (
        <span className="ml-auto">
          {prices.count} model prices ({prices.source})
        </span>
      )}
    </footer>
  );
}
